import { useMutation, useQueryClient } from '@tanstack/react-query'
import { File, X } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { FileUploader } from '@/components/FileUploader'
import { Modal } from '@/components/Modal'
import { createDocument, updateDocument } from '@/services/documents.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const SubmitDocumentModal = observer(({ setOpen, open, editingDocument }) => {
  const { activeEntity } = useContext(UserStoreContext)

  const form = useForm({ defaultValues: editingDocument ?? { name: '', filePath: '' } })
  const [uploadedFileName, setUploadedFileName] = useState(editingDocument?.fileName ?? '')
  const resetForm = () => {
    form.reset({ name: '', filePath: '' })
    setUploadedFileName('')
    setOpen(false)
  }
  const filePath = useWatch({ name: 'filePath', control: form.control })

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: saveDocument } = useMutation({
    mutationFn: async (data) =>
      editingDocument
        ? updateDocument(data.id, {
            name: data.name,
            filePath: data.filePath,
          })
        : createDocument({
            name: data.name || 'Submitted Document',
            entity: activeEntity,
            type: 'Upload',
            filePath: data.filePath,
          }),
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      resetForm()
      handleSuccess(`${data.name} ${editingDocument ? 'updated' : 'created'} successfully`)
      // Refresh certificates list
      queryClient.invalidateQueries({ queryKey: ['accounts', 'documents'] })
    },
  })
  const onSubmit = async (data) => saveDocument(data)

  return (
    <Modal
      open={open}
      title={editingDocument ? 'Edit document' : 'Upload document'}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              disabled={isPending}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input id="name" autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            <FormField
              control={form.control}
              name="filePath"
              render={() => (
                <FormItem>
                  <FormLabel>File</FormLabel>
                  {filePath ? (
                    <div className="flex items-center gap-2">
                      <div className="bg-muted flex min-w-0 flex-1 items-center gap-2 rounded-md border px-3 py-2 text-sm">
                        <File className="text-muted-foreground size-4 shrink-0" />
                        <span className="truncate">
                          {uploadedFileName || editingDocument?.name || filePath}
                        </span>
                        {editingDocument?.signedFilePath && (
                          <a
                            href={editingDocument.signedFilePath}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:text-primary/80 ml-auto shrink-0 text-xs"
                          >
                            Preview
                          </a>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          form.setValue('filePath', '')
                          setUploadedFileName('')
                        }}
                        disabled={isPending}
                      >
                        <X className="size-4" />
                        <span className="sr-only">Remove file</span>
                      </Button>
                    </div>
                  ) : (
                    <FileUploader
                      filePrefix="documents"
                      handleUploadToServer={(fileInfo) => {
                        form.setValue('filePath', fileInfo.fileUploadName)
                        setUploadedFileName(fileInfo.fileName)
                        if (form.getValues('name') === '') {
                          form.setValue('name', fileInfo.fileName)
                        }
                      }}
                      disabled={isPending}
                      className=""
                    />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="mt-8 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" loading={false} disabled={isPending || !filePath}>
                Save document
              </Button>
            </div>
          </form>
        </Form>
      }
      setOpen={setOpen}
    />
  )
})

export default SubmitDocumentModal
